import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  CheckCircle,
  Circle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Upload,
  ExternalLink,
  Copy,
  AlertTriangle,
  Sparkles,
  Mail,
  Database,
  Users,
  Globe,
  Workflow,
  Rocket,
  Palette,
  Plus,
  Trash2,
  X,
  Loader2,
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import {
  WizardStepId,
  useLeadCaptureOnboarding,
  GoLiveChecklistResult,
  ResendDomainCheckResult,
  WizardRotationStrategy,
  WizardRotationRule,
  LeadRoutingConfigInput,
} from '../../hooks/useLeadCaptureOnboarding';
import { useCrmConnection } from '../../hooks/useCrmConnection';
import { useAdConnections } from '../../hooks/useAdConnections';
import { ConnectMetaModal } from '../../components/ads/ConnectMetaModal';
import { ConnectGoogleAdsModal } from '../../components/ads/ConnectGoogleAdsModal';
import { ConnectMyCrmModal } from '../../components/crm/ConnectMyCrmModal';
import { useContactImport } from '../../hooks/useContactImport';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { buildLeadIngestUrl, leadIngestBaseUrl } from './leadIngestUrl';
import { useAuth } from '../../hooks/useAuth';
import { canImportEdmContacts, isEdmEnabled } from '../../utils/edm';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
  // AppLayout passes raw Firestore office docs into the outlet context:
  // each has doc.id (`{id}`) + the data shape from companies/{c}/offices/{officeId}
  // (notably `name`, not `officeName`). Accept both shapes for forward-compat.
  offices?: Array<{ id?: string; name?: string; officeId?: string; officeName?: string }>;
  companyData?: {
    featureFlags?: Record<string, unknown> | null;
    /** Server-written by saveBranding from the tenant's ABN declaration (never by this app). */
    abnVerification?: AbnVerification | null;
  } | null;
};

interface StepDef {
  id: WizardStepId;
  number: number;
  title: string;
  estimatedMinutes: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: React.ComponentType<any>;
  why: string;
}

const STEPS: StepDef[] = [
  {
    id: 'branding',
    number: 1,
    title: 'Upload Your Company Branding',
    estimatedMinutes: '2 min',
    icon: Palette,
    why: 'Your logo and brand colours appear on every email, every report, and every client-facing page. Upload once, applied everywhere.',
  },
  {
    id: 'resend',
    number: 2,
    title: 'Create Your Email Sending Account (Resend)',
    estimatedMinutes: '5 min',
    icon: Mail,
    why: 'Resend is the service that physically delivers your emails. It costs $28 AUD/month for 50,000 emails. You create a free account, then upgrade to Pro.',
  },
  {
    id: 'domain',
    number: 3,
    title: 'Verify Your Email Domain',
    estimatedMinutes: '10-15 min (plus up to 48h for DNS)',
    icon: Globe,
    why: 'This makes emails come from "noreply@yourdomain.com.au" instead of a generic Resend address. Without this, your emails will look suspicious and go to spam.',
  },
  {
    id: 'contacts',
    number: 4,
    title: 'Import Your Existing Contacts',
    estimatedMinutes: '10-20 min',
    icon: Users,
    why: 'Your existing client list from Mailchimp or HubSpot needs to come into Valutten. We also import your suppression list (bounced and unsubscribed emails) to protect your sender reputation.',
  },
  {
    id: 'ad_accounts',
    number: 5,
    title: 'Connect Your Advertising Accounts',
    estimatedMinutes: '5 min',
    icon: Sparkles,
    why: 'Connecting Google and Facebook lets Valutten pull your ad spend data and lead form submissions automatically. This is how we calculate your cost per customer.',
  },
  {
    id: 'mycrm',
    number: 6,
    title: 'Connect MyCRM',
    estimatedMinutes: '5 min',
    icon: Database,
    why: 'This lets Valutten push new leads into MyCRM automatically AND pull settled deal data for trail leakage detection and pipeline analytics.',
  },
  {
    id: 'webhooks',
    number: 7,
    title: 'Connect Your Website Forms',
    estimatedMinutes: '10-15 min',
    icon: Workflow,
    why: 'This makes every website form submission land directly in Valutten as a new lead — with full source attribution and automatic broker assignment.',
  },
  {
    id: 'routing',
    number: 8,
    title: 'Set Up Lead Routing',
    estimatedMinutes: '5 min',
    icon: Users,
    why: 'When a lead comes in, Valutten automatically assigns it to the next broker in rotation. You configure the rules here.',
  },
  {
    id: 'go_live',
    number: 9,
    title: 'Review & Go Live',
    estimatedMinutes: '5 min',
    icon: Rocket,
    why: 'Final checks before your system is live. We verify everything is connected, send a test email, and confirm leads flow end-to-end.',
  },
];

// ---------- Step body components ----------
// Each step's expanded body is a distinct component so the page stays
// readable. Bodies take the `onSave` + `onToggleComplete` callbacks
// implicitly via the context. For Wave 1 the bodies are mostly form shells
// + external links; deeper integrations (DNS checker, CSV upload pipeline)
// land in Wave 2.

// ---------- ABN (branding step) ----------
// The tenant's ABN for their email footer. A new ABN must pass the ATO
// checksum and the tenant must tick a declaration that it is their business's
// ABN; the server records who declared it and when (leadCaptureAction
// saveBranding, no ABN Lookup since 2026-10-26). The record lives on the
// company as `abnVerification` (live via the outlet context's company
// snapshot); this app only shows it. Records from the retired lookup that
// were never accepted ('pending_review', 'unavailable', ...) become usable
// once the tenant ticks the declaration; a VALUTTEN 'rejected' stands.

/** companies/{c}.abnVerification as written by the server. */
export interface AbnVerification {
  abn?: string | null;
  status?: string | null;
  /** 'tenant_declaration', or the retired lookup's 'company_name_match' | 'founder_approval'. */
  basis?: string | null;
  entityName?: string | null;
  checkedAt?: unknown;
}

/** Digits only: spaces and punctuation people type are dropped. */
export function normaliseAbn(raw: string): string {
  return (raw ?? '').replace(/\D/g, '');
}

const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

/**
 * The ATO ABN check: subtract 1 from the first digit, weight the 11 digits,
 * and the sum must divide by 89. Catches typos before a server round trip.
 */
export function isValidAbn(raw: string): boolean {
  const abn = normaliseAbn(raw);
  if (!/^\d{11}$/.test(abn)) return false;
  const digits = abn.split('').map(Number);
  digits[0] -= 1;
  const sum = digits.reduce((acc, d, i) => acc + d * ABN_WEIGHTS[i], 0);
  return sum % 89 === 0;
}

/**
 * True when saving this ABN needs the tenant's declaration: a valid ABN that
 * the stored record does not already settle ('verified', or VALUTTEN's
 * 'rejected', for the same number). Mirrors saveBranding's rule.
 */
export function abnNeedsDeclaration(
  fieldValue: string,
  verification: AbnVerification | null | undefined,
): boolean {
  const abn = normaliseAbn(fieldValue);
  if (!isValidAbn(abn)) return false;
  const sameAbn = !!verification && normaliseAbn(verification.abn ?? '') === abn;
  return !(sameAbn && (verification?.status === 'verified' || verification?.status === 'rejected'));
}

function checkedAtLabel(value: unknown, verb = 'Checked'): string {
  let ms: number | null = null;
  if (typeof value === 'string') ms = Date.parse(value);
  else if (typeof value === 'number') ms = value;
  else if (value && typeof value === 'object') {
    const v = value as { toMillis?: () => number; seconds?: number; _seconds?: number };
    if (typeof v.toMillis === 'function') ms = v.toMillis();
    else if (typeof v.seconds === 'number') ms = v.seconds * 1000;
    else if (typeof v._seconds === 'number') ms = v._seconds * 1000;
  }
  if (ms === null || !Number.isFinite(ms)) return '';
  return ` ${verb} ${new Date(ms).toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short', year: 'numeric' })}.`;
}

export type AbnStatusTone = 'ok' | 'warn' | 'muted';

/**
 * What to show under the ABN field. The stored result only describes the
 * ABN it checked: if the field now holds a different number, it has not
 * been checked yet.
 */
export function describeAbnStatus(
  fieldValue: string,
  verification: AbnVerification | null | undefined,
): { tone: AbnStatusTone; text: string } | null {
  const abn = normaliseAbn(fieldValue);
  if (!abn) return null;
  const sameAbn = !!verification && normaliseAbn(verification.abn ?? '') === abn;
  if (sameAbn && verification?.status === 'verified') {
    const tenantDeclared = verification.basis === 'tenant_declaration';
    const name = verification.entityName?.trim();
    const when = checkedAtLabel(verification.checkedAt, tenantDeclared ? 'Confirmed' : 'Checked');
    return {
      tone: 'ok',
      text: tenantDeclared
        ? `Confirmed as your business ABN. Your emails show it in the footer.${when}`
        : `Verified${name ? `: ${name}` : ''}.${when}`,
    };
  }
  if (sameAbn && verification?.status === 'rejected') {
    return {
      tone: 'warn',
      text: 'VALUTTEN could not confirm this ABN belongs to your company. Contact support or enter your company ABN.',
    };
  }
  return {
    tone: 'muted',
    text: 'Not confirmed yet. Tick the box below to confirm this is your business ABN, then save branding.',
  };
}

const StepBodyBranding: React.FC<{
  brandColor: string;
  setBrandColor: (c: string) => void;
  companyName: string;
  setCompanyName: (s: string) => void;
  abn: string;
  setAbn: (s: string) => void;
  abnError: string | null;
  abnVerification: AbnVerification | null;
  abnDeclared: boolean;
  setAbnDeclared: (v: boolean) => void;
  address: string;
  setAddress: (s: string) => void;
  phone: string;
  setPhone: (s: string) => void;
  replyToEmail: string;
  setReplyToEmail: (s: string) => void;
  replyToError: string | null;
  saveError: string | null;
  saved: boolean;
  logoUrl: string | null;
  uploadLogo: (file: File) => Promise<string>;
  onSave: () => Promise<void>;
  saving: boolean;
}> = ({
  brandColor,
  setBrandColor,
  companyName,
  setCompanyName,
  abn,
  setAbn,
  abnError,
  abnVerification,
  abnDeclared,
  setAbnDeclared,
  address,
  setAddress,
  phone,
  setPhone,
  replyToEmail,
  setReplyToEmail,
  replyToError,
  saveError,
  saved,
  logoUrl,
  uploadLogo,
  onSave,
  saving,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    // Accept PNG / SVG / JPEG up to 4 MB. Firebase Storage rules enforce the
    // final say; this client check just catches typos before burning a
    // network round-trip.
    if (!/^image\/(png|svg\+xml|jpeg|jpg)$/.test(file.type)) {
      setUploadError('Unsupported file type — PNG, SVG, or JPEG only');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setUploadError('File too large — 4 MB max');
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      await uploadLogo(file);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  return (
  <div className="space-y-4">
    <div>
      <label className="block text-xs font-semibold text-gray-400 mb-1">
        Company logo
      </label>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const file = e.dataTransfer.files?.[0];
          if (file) void handleFile(file);
        }}
        onClick={() => fileInputRef.current?.click()}
        className="rounded-lg border-2 border-dashed border-gray-700 p-6 text-center cursor-pointer hover:border-gray-600 hover:bg-gray-800/40 transition-colors"
      >
        {logoUrl ? (
          <div className="flex flex-col items-center gap-3">
            <img
              src={logoUrl}
              alt="Company logo"
              className="max-h-20 max-w-[280px] object-contain"
            />
            <p className="text-xs text-gray-400">
              Click or drop a new file to replace
            </p>
          </div>
        ) : uploading ? (
          <>
            <Loader2 className="w-5 h-5 text-blue-400 mx-auto mb-2 animate-spin" />
            <p className="text-sm text-gray-300">Uploading…</p>
          </>
        ) : (
          <>
            <Upload className="w-5 h-5 text-gray-500 mx-auto mb-2" />
            <p className="text-sm text-gray-400">
              Click or drop PNG / SVG / JPEG, up to 4 MB
            </p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/svg+xml,image/jpeg"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = '';
          }}
        />
      </div>
      {uploadError && (
        <p className="text-xs text-red-400 mt-2">{uploadError}</p>
      )}
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-semibold text-gray-400 mb-1">
          Brand colour
        </label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={brandColor || '#2563eb'}
            onChange={(e) => setBrandColor(e.target.value)}
            className="w-10 h-10 rounded border border-gray-700 bg-transparent"
          />
          <input
            type="text"
            value={brandColor}
            onChange={(e) => setBrandColor(e.target.value)}
            placeholder="#2563eb"
            className="flex-1 px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-sm"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-400 mb-1">
          Company name
        </label>
        <input
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-sm"
        />
      </div>
      <div>
        <label htmlFor="branding-abn" className="block text-xs font-semibold text-gray-400 mb-1">
          ABN
        </label>
        <input
          id="branding-abn"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={abn}
          onChange={(e) => setAbn(e.target.value)}
          placeholder="12 345 678 901"
          aria-describedby="branding-abn-status"
          className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-sm"
        />
        <div id="branding-abn-status">
          {abnError ? (
            <p className="text-xs text-red-400 mt-1">{abnError}</p>
          ) : (
            (() => {
              const st = describeAbnStatus(abn, abnVerification);
              if (!st) return null;
              const Icon = st.tone === 'ok' ? CheckCircle : st.tone === 'warn' ? AlertTriangle : Circle;
              const colour = st.tone === 'ok' ? 'text-emerald-400' : st.tone === 'warn' ? 'text-amber-300' : 'text-gray-400';
              return (
                <p className={`text-xs mt-1 flex items-start gap-1 ${colour}`} data-testid="abn-status">
                  <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{st.text}</span>
                </p>
              );
            })()
          )}
        </div>
        {abnNeedsDeclaration(abn, abnVerification) && (
          <label className="mt-2 flex items-start gap-2 text-xs text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={abnDeclared}
              onChange={(e) => setAbnDeclared(e.target.checked)}
              className="mt-0.5 rounded border-gray-600 bg-gray-800"
              data-testid="abn-declaration"
            />
            <span>
              This is our business&apos;s ABN, and we are responsible for the emails we send with it.
            </span>
          </label>
        )}
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-400 mb-1">
          Phone
        </label>
        <input
          type="text"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-sm"
        />
      </div>
    </div>
    <div>
      <label htmlFor="branding-reply-to" className="block text-xs font-semibold text-gray-400 mb-1">Reply-to email</label>
      <input id="branding-reply-to" type="email" autoComplete="email" value={replyToEmail}
        onChange={(e) => setReplyToEmail(e.target.value)}
        className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-sm" />
      <p className="text-[11px] text-gray-500 mt-1">Replies to campaign and sequence emails go here. Required before you can schedule an eDM.</p>
      {replyToError && <p className="text-xs text-red-400 mt-1">{replyToError}</p>}
    </div>
    <div>
      <label className="block text-xs font-semibold text-gray-400 mb-1">
        Physical address (required by Australian Spam Act)
      </label>
      <input
        type="text"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-sm"
      />
    </div>
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save branding'}
      </button>
      {saveError && <p className="text-xs text-red-400" role="alert">{saveError}</p>}
      {saved && !saveError && <p className="text-xs text-emerald-400">Saved</p>}
    </div>
  </div>
  );
};

const StepBodyResend: React.FC<{
  onSave: (apiKey: string) => Promise<void>;
  configured: boolean;
}> = ({ onSave, configured }) => {
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setError('API key required');
      return;
    }
    setError(null);
    setSaving(true);
    setSaved(false);
    try {
      await onSave(apiKey.trim());
      setApiKey('');
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
  <div className="space-y-4">
    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-300">
      <li>
        <a
          href="https://resend.com"
          target="_blank"
          rel="noreferrer"
          className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
        >
          Go to resend.com <ExternalLink className="w-3 h-3" />
        </a>{' '}
        and click &ldquo;Get Started&rdquo;. Sign up with your work email.
      </li>
      <li>Upgrade to Pro plan ($20 USD / $28 AUD / month, 50,000 emails).</li>
      <li>
        Create an API key in Resend &rarr; API Keys &rarr; Create API Key.
        Name it &ldquo;Valutten&rdquo; and copy the key (it only shows once).
      </li>
      <li>
        Paste your API key below — we store it encrypted in Secret Manager.
      </li>
    </ol>
    {configured && (
      <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-300 flex items-center gap-2">
        <CheckCircle className="w-4 h-4 flex-shrink-0" />
        Resend API key is configured. Paste a new key below to rotate.
      </div>
    )}
    <div className="flex items-center gap-2">
      <input
        type="password"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        placeholder="re_xxxxxxxxxxxxxxxxxxxx"
        className="flex-1 px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-sm"
      />
      <button
        type="button"
        onClick={handleSave}
        disabled={saving || !apiKey.trim()}
        className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
      >
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {saving ? 'Verifying…' : configured ? 'Rotate' : 'Save'}
      </button>
    </div>
    {error && (
      <p className="text-xs text-red-400">{error}</p>
    )}
    {saved && !error && (
      <p className="text-xs text-emerald-400">
        Stored in Secret Manager + readback verified.
      </p>
    )}
    <p className="text-[11px] text-gray-500">
      Stored encrypted per-tenant at{' '}
      <code className="font-mono">resend-apikey-{'{companyId}'}</code>. Only
      the Valutten runtime service account can read it.
    </p>
  </div>
  );
};

const StepBodyDomain: React.FC<{
  onCheck: (domain: string) => Promise<ResendDomainCheckResult>;
  storedState: ResendDomainCheckResult | null;
}> = ({ onCheck, storedState }) => {
  const [domain, setDomain] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<ResendDomainCheckResult | null>(storedState);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (storedState && !result) setResult(storedState);
  }, [storedState, result]);

  const handleCheck = async () => {
    if (!domain.trim()) {
      setError('Domain required');
      return;
    }
    setError(null);
    setChecking(true);
    try {
      const r = await onCheck(domain.trim());
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setChecking(false);
    }
  };

  return (
  <div className="space-y-4">
    <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300 flex gap-2">
      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div>
        You need access to your domain DNS settings. If unsure, ask your web
        developer or domain provider (GoDaddy / Namecheap / Cloudflare /
        Crazy Domains / VentraIP).
      </div>
    </div>
    <p className="text-sm text-gray-300">
      In Resend, go to Domains &rarr; Add Domain, then add these DNS records:
    </p>
    <div className="rounded-md border border-gray-700 bg-gray-900 overflow-hidden text-xs">
      <table className="w-full">
        <thead className="bg-gray-800 text-gray-400">
          <tr>
            <th className="px-3 py-2 text-left">Type</th>
            <th className="px-3 py-2 text-left">Host</th>
            <th className="px-3 py-2 text-left">Value</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800 text-gray-300 font-mono">
          <tr>
            <td className="px-3 py-2">CNAME</td>
            <td className="px-3 py-2">send._domainkey</td>
            <td className="px-3 py-2 text-gray-500">(generated by Resend)</td>
          </tr>
          <tr>
            <td className="px-3 py-2">TXT</td>
            <td className="px-3 py-2">@</td>
            <td className="px-3 py-2">
              v=spf1 include:_spf.google.com include:send.resend.com ~all
            </td>
          </tr>
          <tr>
            <td className="px-3 py-2">CNAME</td>
            <td className="px-3 py-2">resend._domainkey</td>
            <td className="px-3 py-2 text-gray-500">(generated by Resend)</td>
          </tr>
          <tr>
            <td className="px-3 py-2">TXT</td>
            <td className="px-3 py-2">_dmarc</td>
            <td className="px-3 py-2">
              v=DMARC1; p=quarantine; rua=mailto:dmarc@yourdomain.com
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-300 flex gap-2">
      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div>
        <strong>Merge SPF records, don&rsquo;t replace.</strong> Two SPF
        records breaks email. If you use Google Workspace, combine them into
        one:{' '}
        <code className="font-mono">
          v=spf1 include:_spf.google.com include:send.resend.com ~all
        </code>
      </div>
    </div>
    <div className="rounded-md border border-gray-700 bg-gray-800/40 p-3 space-y-2">
      <label className="block text-xs font-semibold text-gray-400">
        Verify with Resend
      </label>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="yourdomain.com.au"
          className="flex-1 px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-sm"
        />
        <button
          type="button"
          onClick={handleCheck}
          disabled={checking || !domain.trim()}
          className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {checking && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {checking ? 'Checking…' : 'Check status'}
        </button>
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
      {result && (
        <div
          className={`rounded-md p-3 text-xs flex items-center gap-2 ${
            result.verified
              ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
              : 'bg-amber-500/10 text-amber-300 border border-amber-500/30'
          }`}
        >
          {result.verified ? (
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          )}
          <span>
            Status: <strong>{result.status}</strong>
            {!result.verified && result.status !== 'not_found' &&
              ' — DNS records may still be propagating (up to 48h).'}
            {result.status === 'not_found' &&
              ' — domain not yet added to your Resend account. Add it at resend.com/domains first.'}
          </span>
        </div>
      )}
    </div>
  </div>
  );
};

const StepBodyContacts: React.FC<{
  companyId: string | undefined;
  edmEnabled: boolean;
  canImportEdm: boolean;
}> = ({ companyId, edmEnabled, canImportEdm }) => {
  const {
    start: startContactsImport,
    status: contactsStatus,
    reset: resetContacts,
  } = useContactImport(companyId);
  const {
    start: startSuppressionsImport,
    status: suppressionsStatus,
    reset: resetSuppressions,
  } = useContactImport(companyId);

  return (
    <div className="space-y-4">
      {edmEnabled && (
        <div className="rounded-md border border-blue-500/30 bg-blue-500/10 p-3 text-xs text-blue-200">
          Importing a list to email it? Use{' '}
          {canImportEdm ? (
            <Link to="/leads/edm/import" className="underline hover:text-blue-100">
              eDM Campaigns › Import contacts
            </Link>
          ) : (
            'eDM Campaigns › Import contacts'
          )}
          . Contacts imported here become leads and are not emailed by eDM. Suppressions imported here also apply to eDM.
        </div>
      )}
      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300 flex gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          DO NOT skip the suppression list import. If you email people who
          previously bounced or unsubscribed, your sender reputation will be
          damaged from day one and all your emails will start going to spam.
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-md border border-gray-700 bg-gray-800/40 p-3">
          <h4 className="font-semibold text-white mb-1">Mailchimp export</h4>
          <p className="text-xs text-gray-400">
            Audience &rarr; All Contacts &rarr; Export Audience &rarr; download
            ZIP &rarr; use subscribed_members_export.csv + bounced +
            unsubscribed CSVs.
          </p>
        </div>
        <div className="rounded-md border border-gray-700 bg-gray-800/40 p-3">
          <h4 className="font-semibold text-white mb-1">HubSpot export</h4>
          <p className="text-xs text-gray-400">
            Contacts &rarr; Export. Also filter Contact unsubscribed=True and
            Email hard bounce=True and export each separately.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ContactsDropzone
          label="Contacts CSV"
          helper="Columns: email*, firstName, lastName, phone, notes, tags. Duplicates (by email or phone) are skipped."
          status={contactsStatus}
          onFile={(file) => startContactsImport(file, 'contacts')}
          onReset={resetContacts}
          disabled={!companyId}
        />
        <ContactsDropzone
          label="Suppression list CSV"
          helper="Columns: email*, reason (bounce / complaint / unsubscribe), notes. Idempotent by email."
          status={suppressionsStatus}
          onFile={(file) => startSuppressionsImport(file, 'suppressions')}
          onReset={resetSuppressions}
          disabled={!companyId}
          variant="warning"
        />
      </div>
    </div>
  );
};

const ContactsDropzone: React.FC<{
  label: string;
  helper: string;
  status: import('../../hooks/useContactImport').ImportStatus | null;
  onFile: (file: File) => Promise<string>;
  onReset: () => void;
  disabled?: boolean;
  variant?: 'default' | 'warning';
}> = ({ label, helper, status, onFile, onReset, disabled, variant = 'default' }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    if (!/\.csv$/i.test(file.name) && file.type !== 'text/csv') {
      setError('CSV files only');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('File too large — 50 MB max');
      return;
    }
    try {
      await onFile(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const borderColor =
    variant === 'warning'
      ? 'border-amber-500/30 hover:border-amber-500/50'
      : 'border-gray-700 hover:border-gray-600';

  const isActive =
    status &&
    (status.status === 'uploading' ||
      status.status === 'queued' ||
      status.status === 'processing');
  const isComplete = status?.status === 'completed';
  const isFailed = status?.status === 'failed';

  return (
    <div className="space-y-2">
      <div>
        <div className="text-xs font-semibold text-gray-300">{label}</div>
        <div className="text-[11px] text-gray-500 mt-0.5">{helper}</div>
      </div>
      {!status && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const file = e.dataTransfer.files?.[0];
            if (file) void handleFile(file);
          }}
          onClick={() => !disabled && fileInputRef.current?.click()}
          className={`rounded-lg border-2 border-dashed ${borderColor} p-5 text-center transition-colors ${
            disabled
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer hover:bg-gray-800/40'
          }`}
        >
          <Upload className="w-5 h-5 text-gray-500 mx-auto mb-2" />
          <p className="text-sm text-gray-400">
            Click or drop a CSV file here
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
              e.target.value = '';
            }}
          />
        </div>
      )}
      {error && <p className="text-xs text-red-400">{error}</p>}
      {status && (
        <div className="rounded-md border border-gray-700 bg-gray-800/40 p-3 space-y-2">
          {isActive && (
            <div>
              <div className="flex items-center gap-2 text-xs text-gray-300 mb-1">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />
                {status.status === 'uploading'
                  ? `Uploading… ${Math.round(status.uploadProgress * 100)}%`
                  : status.status === 'queued'
                    ? 'Queued for processing…'
                    : `Processing… ${status.rowsProcessed}${
                        status.rowsTotal > 0
                          ? ` of ${status.rowsTotal}`
                          : ''
                      } rows`}
              </div>
              <div className="h-1.5 rounded-full bg-gray-900 overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{
                    width: `${
                      status.status === 'uploading'
                        ? status.uploadProgress * 30
                        : status.rowsTotal > 0
                          ? 30 + (status.rowsProcessed / status.rowsTotal) * 70
                          : 30
                    }%`,
                  }}
                />
              </div>
            </div>
          )}
          {isComplete && (
            <div className="text-xs text-emerald-300 flex items-start gap-2">
              <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Import complete</div>
                <div className="text-gray-400 mt-1">
                  {status.rowsProcessed} imported
                  {status.rowsSkipped > 0 && `, ${status.rowsSkipped} skipped`}
                  {status.rowsFailed > 0 && `, ${status.rowsFailed} failed`}
                  {status.truncated && ' (truncated at 50k row cap)'}
                </div>
              </div>
            </div>
          )}
          {isFailed && (
            <div className="text-xs text-red-300 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Import failed</div>
                <div className="text-gray-400 mt-1">
                  {status.error ?? 'unknown error'}
                </div>
              </div>
            </div>
          )}
          {(isComplete || isFailed) && (
            <button
              type="button"
              onClick={onReset}
              className="text-[11px] text-gray-400 hover:text-white underline"
            >
              Upload another
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const StepBodyAdAccounts: React.FC<{
  onConnectMeta: () => void;
  onConnectGoogle: () => void;
  metaConnected: boolean;
  googleConnected: boolean;
}> = ({ onConnectMeta, onConnectGoogle, metaConnected, googleConnected }) => (
  <div className="space-y-3">
    <button
      type="button"
      onClick={onConnectGoogle}
      className="w-full px-4 py-3 rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-left text-sm text-white flex items-center justify-between"
    >
      <span className="inline-flex items-center gap-3">
        <span className="w-7 h-7 rounded bg-[#4285f4] text-white inline-flex items-center justify-center font-bold text-xs">
          G
        </span>
        {googleConnected ? 'Re-connect Google Ads' : 'Connect Google Ads'}
      </span>
      <span className={`text-xs ${googleConnected ? 'text-emerald-400' : 'text-gray-500'}`}>
        {googleConnected ? 'Connected' : 'Paste credentials'}
      </span>
    </button>
    <button
      type="button"
      onClick={onConnectMeta}
      className="w-full px-4 py-3 rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-left text-sm text-white flex items-center justify-between"
    >
      <span className="inline-flex items-center gap-3">
        <span className="w-7 h-7 rounded bg-[#1877f2] text-white inline-flex items-center justify-center font-bold text-xs">
          f
        </span>
        {metaConnected
          ? 'Re-connect Meta (Facebook + Instagram)'
          : 'Connect Facebook + Instagram Ads'}
      </span>
      <span className={`text-xs ${metaConnected ? 'text-emerald-400' : 'text-gray-500'}`}>
        {metaConnected ? 'Connected' : 'Paste credentials'}
      </span>
    </button>
    <p className="text-[11px] text-gray-500">
      OAuth consent flows activate once Meta App Review and Google Ads
      Developer Token Standard Access are approved (3-10 business days and
      1-3 weeks respectively). Paste-credentials path is live today.
    </p>
  </div>
);

const StepBodyMyCrm: React.FC<{
  onConnect: () => void;
  connected: boolean;
}> = ({ onConnect, connected }) => (
  <div className="space-y-3 text-sm">
    <p className="text-gray-300">
      In MyCRM: Profile Management &rarr; API Access Management. Copy your
      Client ID, Client Secret, and your tenant&rsquo;s Okta Auth URL. If you
      don&rsquo;t see this option, contact your Loan Market relationship
      manager.
    </p>
    {connected && (
      <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-300 flex items-center gap-2">
        <CheckCircle className="w-4 h-4" /> MyCRM connected. Daily sync runs at
        05:00 AEST; use &ldquo;Sync Now&rdquo; on the Integrations page to
        trigger manually.
      </div>
    )}
    <button
      type="button"
      onClick={onConnect}
      className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm"
    >
      {connected ? 'Update credentials' : 'Connect MyCRM'}
    </button>
  </div>
);

// Placeholder for the part of the URL this step cannot know. The token is
// revealed once, on the Inbox page, so the wizard shows the SHAPE with the
// company id already filled in rather than a bare endpoint that 400s.
const TOKEN_PLACEHOLDER = 'YOUR_TOKEN';

const StepBodyWebhooks: React.FC<{ companyId: string | undefined }> = ({
  companyId,
}) => {
  const [copied, setCopied] = useState(false);
  // Same builder every other surface uses, so the shape shown here cannot
  // drift from the ready-to-paste URL on the Inbox page.
  const ingestUrl = companyId
    ? buildLeadIngestUrl({ companyId, token: TOKEN_PLACEHOLDER })
    : leadIngestBaseUrl();

  const handleCopy = () => {
    navigator.clipboard
      ?.writeText(ingestUrl)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => undefined);
  };

  return (
  <div className="space-y-3">
    <p className="text-sm text-gray-300">
      VALUTTEN generates a unique webhook URL for your company. Paste it into
      your website form tool&rsquo;s webhook settings. This is for form tools
      only: Facebook Lead Ads and Google Ads lead forms use their own
      endpoints and are connected on the Ad Accounts step.
    </p>
    <p className="text-xs text-amber-300">
      Replace <code className="font-mono">{TOKEN_PLACEHOLDER}</code> with your
      webhook token. Generate it on the Leads Inbox page (Webhook URL), which
      shows the complete URL ready to copy. A URL without the company id and
      token is rejected.
    </p>
    <div className="rounded-md border border-gray-700 bg-gray-900 px-3 py-2 font-mono text-xs text-gray-300 flex items-center justify-between">
      <code className="truncate">{ingestUrl}</code>
      <button
        type="button"
        onClick={handleCopy}
        className="ml-2 p-1 rounded hover:bg-gray-800 text-gray-400 flex items-center gap-1 flex-shrink-0"
        title={copied ? 'Copied!' : 'Copy URL'}
      >
        <Copy className="w-3.5 h-3.5" />
        {copied && <span className="text-[10px] text-emerald-400">Copied</span>}
      </button>
    </div>
    <div className="space-y-2 text-xs text-gray-400">
      <p>
        <strong className="text-gray-300">Gravity Forms (WordPress):</strong>{' '}
        Settings &rarr; Webhooks &rarr; Add New &rarr; paste URL, method POST,
        format JSON, map form fields.
      </p>
      <p>
        <strong className="text-gray-300">Unbounce / generic form tools:</strong>{' '}
        Webhook or POST action &rarr; paste URL, method POST, format JSON.
      </p>
      <p>
        <strong className="text-gray-300">Facebook Lead Ads and Google Ads:</strong>{' '}
        do NOT use this URL. Connect them on the Ad Accounts step; each
        platform posts to its own dedicated endpoint.
      </p>
    </div>
  </div>
  );
};

// ---------- Routing step ----------
// Broker ids in rotations are Firebase Auth UIDs (the id space
// getBrokerLabels + the server-side assignee validators accept), NOT
// commission broker_<hash> doc ids. Office tiers are dead, so a rotation
// matches a single service category (or is company-wide). This mirrors the
// full Lead Routing Rules page (LeadRoutingRulesPage.tsx); the wizard is the
// lightweight get-started surface that seeds rotations without exposing the
// SLA / quiet-hours / geo globals (those stay on the dedicated page and are
// preserved verbatim on save).

interface BrokerOption {
  id: string;
  displayName: string;
}

interface CategoryOption {
  id: string;
  label: string;
}

// UI rotation shape. `serviceCategory`: '' = not chosen yet (blocks save);
// null = company-wide (any category); a non-empty string = a category id.
interface WizardRotation {
  officeId?: string | null;
  dealType?: string | null;
  id: string;
  name: string;
  serviceCategory: string | null;
  strategy: WizardRotationStrategy;
  brokerIds: string[];
}

const ROTATION_STRATEGIES: WizardRotationStrategy[] = [
  'round_robin',
  'least_loaded',
  'random',
  'first_available',
];

// Select sentinel for the company-wide (any category) catch-all. Maps to
// match.serviceCategory = null. Leading underscore guarantees it can never
// collide with a real category id (ID_PATTERN forbids a leading underscore).
const COMPANY_WIDE = '__company_wide__';

// Live service categories used when a tenant has no lead_taxonomy/default doc
// yet. Mirrors the fallback set in LeadRoutingRulesPage so the two surfaces
// never offer divergent categories.
const KNOWN_CATEGORIES: CategoryOption[] = [
  { id: 'home_loan', label: 'Home Loan' },
  { id: 'asset_finance', label: 'Asset Finance' },
  { id: 'buyers_advocacy', label: "Buyer's Advocacy" },
  { id: 'dm_inquiry', label: 'DM Inquiry' },
];

function genRotationId(): string {
  return `rot_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

// Map a stored rotation (rich RotationRuleSchema shape) into the flat UI shape.
// Defensive against partial/legacy docs: a malformed entry maps to null and is
// filtered out by the caller.
function mapStoredRotation(raw: unknown): WizardRotation | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const match =
    o.match && typeof o.match === 'object'
      ? (o.match as Record<string, unknown>)
      : {};
  const sc = match.serviceCategory;
  const strategy = ROTATION_STRATEGIES.includes(
    o.strategy as WizardRotationStrategy
  )
    ? (o.strategy as WizardRotationStrategy)
    : 'round_robin';
  const brokerIds = Array.isArray(o.brokerIds)
    ? o.brokerIds.filter((x): x is string => typeof x === 'string')
    : [];
  return {
    id: typeof o.id === 'string' && o.id ? o.id : genRotationId(),
    name: typeof o.name === 'string' && o.name ? o.name : 'Rotation',
    // Stored string = a category id; stored null/absent = company-wide.
    serviceCategory: typeof sc === 'string' ? sc : null,
    officeId: typeof match.officeId === 'string' ? match.officeId : null,
    dealType: typeof match.dealType === 'string' ? match.dealType : null,
    strategy,
    brokerIds,
  };
}

/**
 * Ordered broker multi-select. Shows display names, stores Auth UIDs. The
 * selection order is the round-robin sequence (reorder controls surface it).
 * Selected UIDs not present in the roster (e.g. a member who has since left)
 * are still shown so they can be removed rather than silently retained.
 */
const RotationBrokerPicker: React.FC<{
  brokers: BrokerOption[];
  loading: boolean;
  selected: string[];
  onChange: (ids: string[]) => void;
}> = ({ brokers, loading, selected, onChange }) => {
  const [filter, setFilter] = useState('');

  const byId = useMemo(() => {
    const m = new Map<string, BrokerOption>();
    brokers.forEach((b) => m.set(b.id, b));
    return m;
  }, [brokers]);

  const toggle = (id: string) => {
    onChange(
      selected.includes(id)
        ? selected.filter((x) => x !== id)
        : [...selected, id]
    );
  };

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= selected.length) return;
    const next = selected.slice();
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return brokers;
    return brokers.filter((b) => b.displayName.toLowerCase().includes(q));
  }, [brokers, filter]);

  const labelFor = (id: string) => byId.get(id)?.displayName ?? id;

  return (
    <div className="space-y-2">
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((id, idx) => {
            const known = byId.has(id);
            return (
              <span
                key={id}
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                  known
                    ? 'border-blue-500/40 bg-blue-500/10 text-blue-200'
                    : 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                }`}
              >
                <span className="text-[10px] font-mono text-gray-400">
                  {idx + 1}.
                </span>
                <span>{labelFor(id)}</span>
                {!known && (
                  <span className="text-[9px] uppercase tracking-wide text-amber-300/80">
                    not in roster
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="text-gray-400 hover:text-white disabled:opacity-30"
                  title="Move earlier"
                >
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => move(idx, 1)}
                  disabled={idx === selected.length - 1}
                  className="text-gray-400 hover:text-white disabled:opacity-30"
                  title="Move later"
                >
                  <ChevronDown className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  className="text-gray-400 hover:text-red-300"
                  title="Remove broker"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      ) : (
        <div className="text-[11px] text-gray-500">
          No brokers selected yet. Tick names below to add them.
        </div>
      )}

      <div className="rounded-md border border-gray-700 bg-gray-900/40">
        {brokers.length > 8 && (
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search brokers by name…"
            className="w-full bg-gray-800 border-b border-gray-700 text-white rounded-t-md px-2 py-1 text-xs focus:outline-none"
          />
        )}
        <div className="max-h-40 overflow-y-auto p-1">
          {loading ? (
            <div className="px-2 py-3 text-xs text-gray-500">
              Loading brokers…
            </div>
          ) : brokers.length === 0 ? (
            <div className="px-2 py-3 text-xs text-gray-500">
              No assignable brokers found for this company.
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-2 py-3 text-xs text-gray-500">
              No brokers match your search.
            </div>
          ) : (
            filtered.map((b) => (
              <label
                key={b.id}
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-800 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(b.id)}
                  onChange={() => toggle(b.id)}
                  className="accent-blue-500 w-3.5 h-3.5"
                />
                <span className="text-xs text-gray-200">{b.displayName}</span>
              </label>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const StepBodyRouting: React.FC<{
  brokers: BrokerOption[];
  brokersLoading: boolean;
  categories: CategoryOption[];
  initial: WizardRotation[];
  initialNotify: boolean;
  onSave: (args: {
    rotations: WizardRotationRule[];
    notifyOnAssignment: boolean;
  }) => Promise<void>;
}> = ({
  brokers,
  brokersLoading,
  categories,
  initial,
  initialNotify,
  onSave,
}) => {
  const [rotations, setRotations] = useState<WizardRotation[]>(initial);
  const [notifyOnAssignment, setNotifyOnAssignment] = useState(initialNotify);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addRotation = () => {
    setRotations((rs) => [
      ...rs,
      {
        id: genRotationId(),
        name: 'New rotation',
        serviceCategory: '',
        strategy: 'round_robin',
        brokerIds: [],
      },
    ]);
    setSaved(false);
  };

  const updateRotation = (id: string, patch: Partial<WizardRotation>) => {
    setRotations((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setSaved(false);
  };

  const removeRotation = (id: string) => {
    setRotations((rs) => rs.filter((r) => r.id !== id));
    setSaved(false);
  };

  // A rotation is invalid until it has a category (or Company-wide) choice AND
  // at least one broker (the server enforces both; the schema requires >= 1
  // brokerId per rotation).
  const hasInvalidRotation = rotations.some(
    (r) => r.serviceCategory === '' || r.brokerIds.length === 0
  );

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    setSaved(false);
    try {
      const payload: WizardRotationRule[] = rotations.map((r) => ({
        id: r.id,
        name: r.name.trim() || 'Rotation',
        match: {
          // '' is blocked by hasInvalidRotation; treat any residual '' as
          // company-wide defensively so we never send an invalid category.
          serviceCategory: r.serviceCategory === '' ? null : r.serviceCategory,
          dealType: r.dealType ?? null,
          officeId: r.officeId ?? null,
        },
        strategy: r.strategy,
        brokerIds: r.brokerIds,
      }));
      await onSave({ rotations: payload, notifyOnAssignment });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-300">
        Create a rotation for each service category (or one company-wide
        rotation). Tick the brokers who should receive those leads and use the
        arrows to set the rotation order. Leads route to the next broker in the
        list, wrapping around.
      </p>

      {rotations.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-700 bg-gray-800/40 p-6 text-center text-xs text-gray-400">
          No rotations yet. Click <strong>Add rotation</strong> to create one
          for a service category (or company-wide).
        </div>
      ) : (
        <div className="space-y-3">
          {rotations.map((r) => {
            const scValue =
              r.serviceCategory === ''
                ? ''
                : r.serviceCategory === null
                  ? COMPANY_WIDE
                  : r.serviceCategory;
            // Guarantee the stored category is always selectable (round-trips a
            // live value even when it is absent from the taxonomy list).
            const extraCat =
              typeof r.serviceCategory === 'string' &&
              r.serviceCategory !== '' &&
              !categories.some((c) => c.id === r.serviceCategory)
                ? [{ id: r.serviceCategory, label: r.serviceCategory }]
                : [];
            const catOpts = [...categories, ...extraCat];

            const missing: string[] = [];
            if (r.serviceCategory === '')
              missing.push('Choose a service category (or Company-wide).');
            if (r.brokerIds.length === 0)
              missing.push('Add at least one broker.');

            return (
              <div
                key={r.id}
                className="rounded-md border border-gray-700 bg-gray-800/40 p-3"
              >
                <div className="flex items-start gap-2 mb-3">
                  <input
                    type="text"
                    value={r.name}
                    onChange={(e) =>
                      updateRotation(r.id, { name: e.target.value })
                    }
                    className="flex-1 px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-white text-sm font-semibold"
                    placeholder="Rotation name (e.g. Home Loan rotation)"
                  />
                  <button
                    type="button"
                    onClick={() => removeRotation(r.id)}
                    className="text-gray-400 hover:text-red-400 p-1.5"
                    title="Remove rotation"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                      Service category
                    </label>
                    <select
                      value={scValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        const next =
                          v === '' ? '' : v === COMPANY_WIDE ? null : v;
                        updateRotation(r.id, { serviceCategory: next, dealType: null });
                      }}
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
                    >
                      <option value="" disabled>
                        Choose a category…
                      </option>
                      <option value={COMPANY_WIDE}>
                        Company-wide (any category)
                      </option>
                      {catOpts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                      Strategy
                    </label>
                    <select
                      value={r.strategy}
                      onChange={(e) =>
                        updateRotation(r.id, {
                          strategy: e.target.value as WizardRotationStrategy,
                        })
                      }
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
                    >
                      <option value="round_robin">Round-robin</option>
                      <option value="least_loaded">Least-loaded</option>
                      <option value="random">Random</option>
                      <option value="first_available">First-available</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                    Brokers in rotation (assignment order)
                  </label>
                  <RotationBrokerPicker
                    brokers={brokers}
                    loading={brokersLoading}
                    selected={r.brokerIds}
                    onChange={(ids) => updateRotation(r.id, { brokerIds: ids })}
                  />
                </div>

                {missing.length > 0 && (
                  <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-300">
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{missing.join(' ')}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={addRotation}
        className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 hover:bg-gray-700 text-white text-xs inline-flex items-center gap-1"
      >
        <Plus className="w-3 h-3" /> Add rotation
      </button>

      <label className="flex items-center gap-2 text-sm text-gray-300">
        <input
          type="checkbox"
          checked={notifyOnAssignment}
          onChange={(e) => {
            setNotifyOnAssignment(e.target.checked);
            setSaved(false);
          }}
          className="w-4 h-4"
        />
        Email notification when a lead is assigned
      </label>

      <div className="flex items-center justify-end gap-2">
        {hasInvalidRotation && (
          <span className="text-xs text-amber-300">
            Each rotation needs a category (or Company-wide) and at least one
            broker.
          </span>
        )}
        {saved && !error && (
          <span className="text-xs text-emerald-400">Routing rules saved.</span>
        )}
        {error && <span className="text-xs text-red-400">{error}</span>}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || hasInvalidRotation}
          className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {saving ? 'Saving…' : 'Save routing'}
        </button>
      </div>
    </div>
  );
};

const StepBodyGoLive: React.FC<{
  loadChecklist: () => Promise<GoLiveChecklistResult>;
}> = ({ loadChecklist }) => {
  const [state, setState] = useState<GoLiveChecklistResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadChecklist()
      .then((r) => {
        if (!cancelled) setState(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadChecklist]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading checklist…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-300">
        {error}
      </div>
    );
  }
  if (!state) return null;

  return (
    <div className="space-y-3">
      <ul className="space-y-2 text-sm">
        {state.checklist.map((item) => (
          <li
            key={item.key}
            className={`flex items-start gap-2 ${
              item.completed ? 'text-emerald-300' : 'text-gray-400'
            }`}
          >
            {item.completed ? (
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            ) : (
              <Circle className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span>{item.label}</span>
                {item.required === false && (
                  <span className="rounded border border-gray-600 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-gray-400">
                    Optional
                  </span>
                )}
              </div>
              {item.detail && (
                <div className="text-[11px] text-gray-500 mt-0.5">
                  {item.detail}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
      <div
        className={`rounded-md p-3 text-sm flex items-center gap-2 ${
          state.readyToGoLive
            ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
            : 'bg-blue-500/5 text-blue-300 border border-blue-500/30'
        }`}
      >
        {state.readyToGoLive ? (
          <>
            <Rocket className="w-4 h-4 flex-shrink-0" />
            <span>
              All {state.totalCount} required checks complete. Your Lead
              Capture system is ready to go live.
            </span>
          </>
        ) : (
          <span>
            {state.completedCount} of {state.totalCount} required checks
            complete. Finish the remaining steps above, then refresh this
            page. Items marked Optional do not block go-live.
          </span>
        )}
      </div>
    </div>
  );
};

interface StepRenderForm {
  companyId: string | undefined;
  brandColor: string;
  setBrandColor: (c: string) => void;
  companyName: string;
  setCompanyName: (s: string) => void;
  abn: string;
  setAbn: (s: string) => void;
  abnError: string | null;
  abnVerification: AbnVerification | null;
  abnDeclared: boolean;
  setAbnDeclared: (v: boolean) => void;
  address: string;
  setAddress: (s: string) => void;
  phone: string;
  setPhone: (s: string) => void;
  replyToEmail: string;
  setReplyToEmail: (s: string) => void;
  replyToError: string | null;
  brandingSaveError: string | null;
  brandingSaved: boolean;
  edmEnabled: boolean;
  canImportEdm: boolean;
  logoUrl: string | null;
  onSaveBranding: () => Promise<void>;
  saving: boolean;
  uploadLogo: (file: File) => Promise<string>;
  saveResendApiKey: (apiKey: string) => Promise<void>;
  resendConfigured: boolean;
  checkResendDomain: (domain: string) => Promise<ResendDomainCheckResult>;
  resendDomainState: ResendDomainCheckResult | null;
  loadGoLiveChecklist: () => Promise<GoLiveChecklistResult>;
  onConnectMeta: () => void;
  onConnectGoogle: () => void;
  metaConnected: boolean;
  googleConnected: boolean;
  onConnectMyCrm: () => void;
  myCrmConnected: boolean;
  brokers: BrokerOption[];
  brokersLoading: boolean;
  categories: CategoryOption[];
  initialRotations: WizardRotation[];
  initialNotify: boolean;
  onSaveRouting: (args: {
    rotations: WizardRotationRule[];
    notifyOnAssignment: boolean;
  }) => Promise<void>;
}

function renderStepBody(step: StepDef, form: StepRenderForm): React.ReactElement {
  switch (step.id) {
    case 'branding':
      return (
        <StepBodyBranding
          brandColor={form.brandColor}
          setBrandColor={form.setBrandColor}
          companyName={form.companyName}
          setCompanyName={form.setCompanyName}
          abn={form.abn}
          setAbn={form.setAbn}
          abnError={form.abnError}
          abnVerification={form.abnVerification}
          abnDeclared={form.abnDeclared}
          setAbnDeclared={form.setAbnDeclared}
          address={form.address}
          setAddress={form.setAddress}
          phone={form.phone}
          setPhone={form.setPhone}
          replyToEmail={form.replyToEmail}
          setReplyToEmail={form.setReplyToEmail}
          replyToError={form.replyToError}
          saveError={form.brandingSaveError}
          saved={form.brandingSaved}
          logoUrl={form.logoUrl}
          uploadLogo={form.uploadLogo}
          onSave={form.onSaveBranding}
          saving={form.saving}
        />
      );
    case 'resend':
      return (
        <StepBodyResend
          onSave={form.saveResendApiKey}
          configured={form.resendConfigured}
        />
      );
    case 'domain':
      return (
        <StepBodyDomain
          onCheck={form.checkResendDomain}
          storedState={form.resendDomainState}
        />
      );
    case 'contacts':
      return (
        <StepBodyContacts
          companyId={form.companyId}
          edmEnabled={form.edmEnabled}
          canImportEdm={form.canImportEdm}
        />
      );
    case 'ad_accounts':
      return (
        <StepBodyAdAccounts
          onConnectMeta={form.onConnectMeta}
          onConnectGoogle={form.onConnectGoogle}
          metaConnected={form.metaConnected}
          googleConnected={form.googleConnected}
        />
      );
    case 'mycrm':
      return (
        <StepBodyMyCrm
          onConnect={form.onConnectMyCrm}
          connected={form.myCrmConnected}
        />
      );
    case 'webhooks':
      return <StepBodyWebhooks companyId={form.companyId} />;
    case 'routing':
      return (
        <StepBodyRouting
          brokers={form.brokers}
          brokersLoading={form.brokersLoading}
          categories={form.categories}
          initial={form.initialRotations}
          initialNotify={form.initialNotify}
          onSave={form.onSaveRouting}
        />
      );
    case 'go_live':
      return <StepBodyGoLive loadChecklist={form.loadGoLiveChecklist} />;
    default:
      return <></>;
  }
}

// Credential / sending-account steps marketing cannot operate; they own the
// webhook + lead-capture steps, not the Resend / domain / MyCRM credentials
// (marketing RBAC §6 wizard step filter).
// 'routing' is NOW visible to marketing: the broker picker sources the roster
// from the getBrokerLabels callable (Auth-UID id space, which permits the
// marketing role) rather than the commission-bearing /brokers collection, so
// marketing can configure routing rotations here as well as on the dedicated
// Routing Rules page.
const MARKETING_HIDDEN_STEP_IDS: WizardStepId[] = ['resend', 'domain', 'mycrm'];

const LeadCaptureSetupWizardPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const companyId = ctx.userData?.companyId;
  const isMarketing = ctx.userData?.role === 'marketing';
  const { claims } = useAuth();
  const edmEnabled = isEdmEnabled(ctx.companyData?.featureFlags);
  const canImportEdm = canImportEdmContacts(
    ctx.userData?.role ?? claims?.role,
    claims?.canSeeAllOffices
  );
  // Marketing only sees the steps it can act on; credential steps are hidden.
  const visibleSteps = useMemo(
    () =>
      isMarketing
        ? STEPS.filter((s) => !MARKETING_HIDDEN_STEP_IDS.includes(s.id))
        : STEPS,
    [isMarketing]
  );

  const {
    data,
    loading,
    error,
    markStepComplete,
    saveBranding,
    saveLeadRoutingConfig,
    refresh,
    uploadLogo,
    saveResendApiKey,
    checkResendDomain,
    getGoLiveChecklist,
  } = useLeadCaptureOnboarding(companyId);
  const { connections: crmConnections } = useCrmConnection(companyId);
  const { connections: adConnections } = useAdConnections(companyId);

  const [expandedSteps, setExpandedSteps] = useState<Set<WizardStepId>>(
    new Set(['branding'])
  );
  const [brandingForm, setBrandingForm] = useState({
    brandColor: '',
    companyName: '',
    abn: '',
    address: '',
    phone: '',
    replyToEmail: '',
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [replyToError, setReplyToError] = useState<string | null>(null);
  const [abnError, setAbnError] = useState<string | null>(null);
  // The tenant's declaration for the ABN in the field; cleared whenever the
  // field changes, so each ABN is declared explicitly.
  const [abnDeclared, setAbnDeclared] = useState(false);
  const [brandingSaveError, setBrandingSaveError] = useState<string | null>(null);
  const [brandingSaved, setBrandingSaved] = useState(false);
  // Track the Resend domain state locally. The hook's `data.branding` only
  // carries branding; the domain verification result is a separate config
  // doc. First call to checkResendDomain populates this; subsequent calls
  // refresh it.
  const [resendDomainState, setResendDomainState] = useState<ResendDomainCheckResult | null>(null);

  // Modal visibility.
  const [metaModalOpen, setMetaModalOpen] = useState(false);
  const [googleModalOpen, setGoogleModalOpen] = useState(false);
  const [myCrmModalOpen, setMyCrmModalOpen] = useState(false);

  // Routing-step inputs. Brokers come from the getBrokerLabels callable (Auth
  // UIDs, the id space getBrokerLabels + the server-side assignee validators
  // accept), NOT the commission-bearing /brokers collection (broker_<hash> doc
  // ids, wrong id space + denied to marketing). Categories come from the
  // tenant's lead_taxonomy/default doc (fallback: KNOWN_CATEGORIES). The full
  // existing routing config is retained so the wizard preserves globals owned
  // by the dedicated Routing Rules page (quiet hours, SLA, geo) on save.
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);
  const [brokersLoading, setBrokersLoading] = useState(true);
  const [categories, setCategories] = useState<CategoryOption[]>(KNOWN_CATEGORIES);
  const [existingRoutingConfig, setExistingRoutingConfig] = useState<
    Record<string, unknown>
  >({});
  const [initialRotations, setInitialRotations] = useState<WizardRotation[]>([]);
  const [initialNotify, setInitialNotify] = useState(true);
  // Routing-config read outcome. The wizard SAVES the whole routing document
  // (it preserves globals owned by the Routing Rules page), so a failed read
  // followed by a save would write those globals away. `null` = not attempted
  // yet, false = the read FAILED (block the save), true = we have the real
  // document (or confirmed there is none yet).
  const [routingConfigLoaded, setRoutingConfigLoaded] = useState<boolean | null>(
    null
  );
  // Optimistic-lock value for saveLeadRoutingConfig. The server compares it
  // against the stored updatedAt and throws 'aborted' on a mismatch, which is
  // what stops the wizard clobbering a concurrent edit from the Routing Rules
  // page. The wizard never sent it, so it always won the race silently.
  const [routingExpectedUpdatedAt, setRoutingExpectedUpdatedAt] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setBrokersLoading(true);
    (async () => {
      // 1) Broker roster via getBrokerLabels (permits the marketing role;
      //    returns { id (=Auth UID), displayName, officeIds } for active
      //    assignable members). Non-fatal: the picker degrades to empty.
      try {
        const fn = httpsCallable<
          { companyId: string },
          { brokers: Array<{ id: string; displayName?: string; officeIds?: string[] }> }
        >(functions, 'getBrokerLabels');
        const res = await fn({ companyId });
        if (!cancelled) {
          const list = (res.data?.brokers ?? []).map((b) => ({
            id: b.id,
            displayName: b.displayName?.trim() || b.id,
          }));
          list.sort((a, b) => a.displayName.localeCompare(b.displayName));
          setBrokers(list);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[SetupWizard] getBrokerLabels failed (non-fatal)', err);
          setBrokers([]);
        }
      } finally {
        if (!cancelled) setBrokersLoading(false);
      }

      // 2) Existing routing config: preserve globals; seed rotations + notify.
      try {
        const cfgSnap = await getDoc(
          doc(db, `companies/${companyId}/lead_routing_rules/default`)
        );
        if (!cancelled && !cfgSnap.exists()) {
          // No config yet: a first save is legitimate, and the lock value is
          // null (which the server reads as "expected absent").
          setRoutingConfigLoaded(true);
          setRoutingExpectedUpdatedAt(null);
        }
        if (!cancelled && cfgSnap.exists()) {
          const raw = cfgSnap.data() as Record<string, unknown>;
          const storedUpdatedAt = raw.updatedAt as
            | { toMillis?: () => number }
            | undefined;
          setRoutingExpectedUpdatedAt(storedUpdatedAt?.toMillis?.() ?? null);
          setRoutingConfigLoaded(true);
          // Strip server-managed keys: the server re-stamps updatedAt/By and
          // never lets admins rewrite the rotationCursors it advances.
          const editable: Record<string, unknown> = { ...raw };
          delete editable.rotationCursors;
          delete editable.updatedAt;
          delete editable.updatedBy;
          setExistingRoutingConfig(editable);
          const rawRotations = Array.isArray(raw.rotations) ? raw.rotations : [];
          setInitialRotations(
            rawRotations
              .map(mapStoredRotation)
              .filter((r): r is WizardRotation => r !== null)
          );
          const notify = raw.notifyOnAssignment as { email?: boolean } | undefined;
          // Runtime treats the flag as ON unless explicitly false.
          setInitialNotify(notify?.email !== false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[SetupWizard] routing config fetch failed', err);
          // Mark the read as failed so the routing step refuses to save over
          // a config it could not read.
          setRoutingConfigLoaded(false);
        }
      }

      // 3) Service categories from lead_taxonomy/default (fallback: known set).
      try {
        const taxSnap = await getDoc(
          doc(db, `companies/${companyId}/lead_taxonomy/default`)
        );
        if (!cancelled) {
          if (taxSnap.exists()) {
            const d = taxSnap.data() as {
              serviceCategories?: Array<{
                id?: string;
                label?: string;
                order?: number;
                active?: boolean;
              }>;
            };
            const cats = (d.serviceCategories ?? [])
              .filter(
                (
                  c
                ): c is { id: string; label?: string; order?: number; active?: boolean } =>
                  !!c && typeof c.id === 'string' && c.active !== false
              )
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((c) => ({ id: c.id, label: c.label?.trim() || c.id }));
            setCategories(cats.length > 0 ? cats : KNOWN_CATEGORIES);
          } else {
            setCategories(KNOWN_CATEGORIES);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.warn(
            '[SetupWizard] lead_taxonomy read failed; using known categories',
            err
          );
          setCategories(KNOWN_CATEGORIES);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Monday.com OAuth callback handler. The user lands here on
  // `/leads/setup?code=...&state=monday-...` after authorising in the
  // Monday popup. We:
  //   1. Detect a `code` + `state` query param.
  //   2. Verify `state` matches the CSRF token we stashed in sessionStorage
  //      and starts with the `monday-` prefix (so other OAuth flows added
  //      later won't trigger this handler).
  //   3. Call `connectMonday` with the code + redirectUri.
  //   4. Strip the query params from the URL so a refresh doesn't replay.
  //
  // Stays scoped to Monday — extending this handler for additional
  // providers means matching their own `state` prefix.
  React.useEffect(() => {
    if (!companyId) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) return;
    if (!state.startsWith('monday-')) return;

    interface StoredOAuthState {
      state?: string;
      companyId?: string;
      redirectUri?: string;
    }
    let stored: StoredOAuthState | null = null;
    try {
      const raw = sessionStorage.getItem('monday-oauth-state');
      stored = raw ? (JSON.parse(raw) as StoredOAuthState) : null;
    } catch (err) {
      console.warn('[SetupWizard] Failed to read monday OAuth state', err);
    }

    const stripQueryParams = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      window.history.replaceState({}, '', url.toString());
    };

    if (!stored || stored.state !== state || stored.companyId !== companyId) {
      console.warn('[SetupWizard] monday OAuth state mismatch — ignoring callback');
      stripQueryParams();
      return;
    }

    const redirectUri = stored.redirectUri ?? `${window.location.origin}/leads/setup`;

    (async () => {
      try {
        const fn = httpsCallable<
          { action: 'connectMonday'; companyId: string; code: string; redirectUri: string },
          { success: boolean; connectionId: string }
        >(functions, 'connectMondayCredentials');
        await fn({ action: 'connectMonday', companyId, code, redirectUri });
      } catch (err) {
        console.error('[SetupWizard] connectMonday failed', err);
        window.alert(
          `Monday.com connection failed: ${err instanceof Error ? err.message : String(err)}`
        );
      } finally {
        try {
          sessionStorage.removeItem('monday-oauth-state');
        } catch {
          // ignore
        }
        stripQueryParams();
      }
    })();
  }, [companyId]);

  // Google Ads OAuth callback handler. State is server-issued (via
  // prepareGoogleAdsOAuth) and round-tripped through Google. We only
  // read the matching state token + companyId hint from sessionStorage
  // for CSRF verification — the actual OAuth context (customerIds,
  // loginCustomerId) is read server-side from the pending state doc
  // when completeGoogleAdsOAuth is called.
  React.useEffect(() => {
    if (!companyId) return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) return;

    interface StoredGoogleAdsOAuthState {
      state?: string;
      companyId?: string;
      redirectUri?: string;
    }
    let stored: StoredGoogleAdsOAuthState | null = null;
    try {
      const raw = sessionStorage.getItem('googleads-oauth-state');
      stored = raw ? (JSON.parse(raw) as StoredGoogleAdsOAuthState) : null;
    } catch (err) {
      console.warn('[SetupWizard] Failed to read Google Ads OAuth state', err);
    }
    // Only this branch handles Google's callback. Monday uses the
    // separate `monday-` prefix branch above.
    if (!stored || stored.state !== state) return;

    const stripQueryParams = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      url.searchParams.delete('scope');
      url.searchParams.delete('authuser');
      url.searchParams.delete('prompt');
      window.history.replaceState({}, '', url.toString());
    };

    if (stored.companyId !== companyId) {
      console.warn('[SetupWizard] Google Ads OAuth companyId mismatch — ignoring');
      stripQueryParams();
      return;
    }

    const redirectUri = stored.redirectUri ?? `${window.location.origin}/leads/setup`;

    (async () => {
      try {
        const fn = httpsCallable<
          {
            action: 'completeGoogleAdsOAuth';
            companyId: string;
            code: string;
            state: string;
            redirectUri: string;
          },
          { success: boolean; connectionId: string }
        >(functions, 'connectAdCredentials');
        await fn({
          action: 'completeGoogleAdsOAuth',
          companyId,
          code,
          state,
          redirectUri,
        });
      } catch (err) {
        console.error('[SetupWizard] completeGoogleAdsOAuth failed', err);
        window.alert(
          `Google Ads connection failed: ${err instanceof Error ? err.message : String(err)}`
        );
      } finally {
        try {
          sessionStorage.removeItem('googleads-oauth-state');
        } catch {
          // ignore
        }
        stripQueryParams();
      }
    })();
  }, [companyId]);

  const handleCheckResendDomain = React.useCallback(
    async (domain: string) => {
      const r = await checkResendDomain(domain);
      setResendDomainState(r);
      return r;
    },
    [checkResendDomain]
  );

  const handleSaveResendApiKey = React.useCallback(
    async (apiKey: string) => {
      await saveResendApiKey(apiKey);
    },
    [saveResendApiKey]
  );

  const handleSaveRouting = React.useCallback(
    async (args: {
      rotations: WizardRotationRule[];
      notifyOnAssignment: boolean;
    }) => {
      // Never save over a config we failed to read. This callback writes the
      // WHOLE routing document, so saving from the empty fallback state would
      // wipe the globals owned by the Routing Rules page (quiet hours, SLA,
      // geo) that the merge below is meant to preserve.
      if (routingConfigLoaded === false) {
        throw new Error(
          'Could not read the current routing rules, so saving was blocked to avoid overwriting them. Reload the page and try again.'
        );
      }
      // Merge our rotations + the email-notify toggle into the retained config
      // so globals owned by the dedicated Routing Rules page (quiet hours, SLA,
      // geo) are preserved. The server persists with merge:true, so any field
      // we omit here would be re-defaulted and silently clobber a stored value.
      const priorNotify =
        existingRoutingConfig.notifyOnAssignment &&
        typeof existingRoutingConfig.notifyOnAssignment === 'object'
          ? (existingRoutingConfig.notifyOnAssignment as {
              email?: boolean;
              inApp?: boolean;
              sms?: boolean;
            })
          : {};
      const config: LeadRoutingConfigInput = {
        ...existingRoutingConfig,
        rotations: args.rotations,
        notifyOnAssignment: { ...priorNotify, email: args.notifyOnAssignment },
      };
      // Send the optimistic lock so a concurrent edit from the Routing Rules
      // page is rejected ('aborted') rather than silently overwritten.
      await saveLeadRoutingConfig(config, routingExpectedUpdatedAt);
      // A successful save re-stamps updatedAt, so the lock value we hold is
      // now stale and a second save from this same mount would be rejected as
      // a conflict. Re-read it.
      try {
        const after = await getDoc(
          doc(db, `companies/${companyId}/lead_routing_rules/default`)
        );
        const stamped = after.data()?.updatedAt as
          | { toMillis?: () => number }
          | undefined;
        setRoutingExpectedUpdatedAt(stamped?.toMillis?.() ?? null);
      } catch (err) {
        // Could not refresh the lock: block further saves from this mount
        // rather than send a value we know is stale.
        console.error('[SetupWizard] routing lock refresh failed', err);
        setRoutingConfigLoaded(false);
      }
      // Reflect the saved state locally so a step collapse/re-expand re-seeds
      // the just-saved rotations (the step body seeds from `initial` on mount).
      setExistingRoutingConfig(config);
      setInitialRotations(
        args.rotations.map((r) => ({
          id: r.id,
          name: r.name,
          serviceCategory: r.match.serviceCategory,
          officeId: r.match.officeId,
          dealType: r.match.dealType,
          strategy: r.strategy,
          brokerIds: r.brokerIds,
        }))
      );
      setInitialNotify(args.notifyOnAssignment);
    },
    [saveLeadRoutingConfig, existingRoutingConfig, routingConfigLoaded, routingExpectedUpdatedAt, companyId]
  );

  const myCrmConnected = crmConnections.some(
    (c) => c.provider === 'mycrm' && c.status === 'active'
  );
  const metaConnected = adConnections.some(
    (c) => c.provider === 'meta_ads' && c.status === 'active'
  );
  const googleConnected = adConnections.some(
    (c) => c.provider === 'google_ads' && c.status === 'active'
  );

  // Hydrate branding form from Firestore state on first load.
  React.useEffect(() => {
    if (!data?.branding) return;
    setBrandingForm({
      brandColor: data.branding.brandColor ?? '',
      companyName: data.branding.companyName ?? '',
      abn: data.branding.abn ?? '',
      address: data.branding.address ?? '',
      phone: data.branding.phone ?? '',
      replyToEmail: data.branding.replyToEmail ?? '',
    });
  }, [data?.branding]);

  const progressPercent = data?.progressPercent ?? 0;
  const completedCount = useMemo(
    () =>
      data
        ? visibleSteps.filter((step) => data.steps[step.id]?.completed).length
        : 0,
    [data, visibleSteps]
  );

  const toggleExpanded = (id: WizardStepId) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleComplete = async (id: WizardStepId, current: boolean) => {
    await markStepComplete(id, !current);
  };

  const handleSaveBranding = async () => {
    setBrandingSaved(false);
    setBrandingSaveError(null);
    // Reply-to is sent only when it differs from the stored value, so an
    // unrelated branding save never re-submits (and re-checks) it. When sent
    // it is trimmed, and '' clears it: it never uses the `|| undefined` the
    // other fields use (undefined means "leave unchanged").
    const replyToEmail = brandingForm.replyToEmail.trim();
    const replyToChanged = replyToEmail !== (data?.branding?.replyToEmail ?? '');
    if (replyToChanged && replyToEmail !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(replyToEmail)) {
      setReplyToError('Enter a valid email address');
      return;
    }
    setReplyToError(null);
    // ABN: checked only when it differs from the stored value, so a tenant
    // with an older stored value can still save the rest of the branding.
    // A changed ABN is sent as digits only. A valid ABN the stored record does
    // not settle needs the declaration tick (abnNeedsDeclaration, the same
    // rule as saveBranding): when ticked, the ABN is sent with
    // abnDeclaration even if unchanged, so an older unconfirmed one can be
    // confirmed.
    const abnDigits = normaliseAbn(brandingForm.abn);
    const abnChanged = abnDigits !== normaliseAbn(data?.branding?.abn ?? '');
    if (abnChanged && abnDigits !== '' && !isValidAbn(abnDigits)) {
      setAbnError('That is not a valid ABN. Check the 11 digits and try again.');
      return;
    }
    const declarationNeeded = abnNeedsDeclaration(abnDigits, ctx.companyData?.abnVerification ?? null);
    if (abnChanged && declarationNeeded && !abnDeclared) {
      setAbnError('Tick the box to confirm this is your business ABN.');
      return;
    }
    const sendAbnDeclaration = declarationNeeded && abnDeclared;
    setAbnError(null);
    setSavingBranding(true);
    try {
      // Same rule as reply-to for every text field: send it only when it
      // differs from the stored value, trimmed, and send '' to CLEAR it. The
      // previous `field || undefined` could never clear anything (undefined
      // means "unchanged" on the server), so an emptied field was silently
      // kept while the page reported "Saved".
      const stored = data?.branding;
      const changedText = (
        key: 'brandColor' | 'companyName' | 'address' | 'phone',
      ): Record<string, string> => {
        const next = brandingForm[key].trim();
        // Trim both sides: a stored value with stray whitespace must not be
        // re-sent (and audited) on every no-op save.
        const current = (stored?.[key] ?? '').trim();
        return next !== current ? { [key]: next } : {};
      };
      const payload = {
        ...changedText('brandColor'),
        ...changedText('companyName'),
        // ABN compares digits (abnChanged above) and is sent as digits, or ''
        // to clear it and its record; with the declaration when ticked.
        ...(abnChanged || sendAbnDeclaration ? { abn: abnDigits } : {}),
        ...(sendAbnDeclaration ? { abnDeclaration: true } : {}),
        ...changedText('address'),
        ...changedText('phone'),
        ...(replyToChanged ? { replyToEmail } : {}),
      };
      if (Object.keys(payload).length === 0) {
        // Nothing changed: no call, no updatedAt bump, no audit row.
        setBrandingSaved(true);
        return;
      }
      await saveBranding(payload);
      setAbnDeclared(false);
      setBrandingSaved(true);
    } catch (err) {
      const e = err as { code?: unknown; details?: { code?: unknown } } | null;
      const code = e?.code;
      // The server re-checks the ABN (saveBranding `abn_invalid`,
      // `abn_declaration_required`); show it on the ABN field, not as a
      // reply-to problem.
      if (e?.details?.code === 'abn_invalid') {
        setAbnError('That is not a valid ABN. Check the 11 digits and try again.');
        setBrandingSaveError('Branding was not saved. Check the ABN.');
        return;
      }
      if (e?.details?.code === 'abn_declaration_required') {
        setAbnError('Tick the box to confirm this is your business ABN.');
        setBrandingSaveError('Branding was not saved. Confirm the ABN.');
        return;
      }
      if (e?.details?.code === 'abn_rejected') {
        setAbnError('VALUTTEN could not confirm this ABN belongs to your company. Contact support or enter your company ABN.');
        setBrandingSaveError('Branding was not saved. Check the ABN.');
        return;
      }
      setBrandingSaveError(
        e?.details?.code === 'reply_to_not_bound'
          ? "That address must be your company email or a team member's email."
          : code === 'functions/invalid-argument' || code === 'invalid-argument'
            ? 'One of the branding fields was not accepted. Check them and try again.'
            : 'Branding was not saved. Please try again.'
      );
    } finally {
      setSavingBranding(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={CheckCircle}
        title="Setup Wizard"
        subtitle="Follow each step to get your lead capture, email, and CRM fully connected — no technical knowledge required"
        onRefresh={refresh}
        isRefreshing={loading}
      />

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Progress bar */}
      <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-wide text-gray-400">
            Setup progress
          </span>
          <span className="text-sm font-semibold text-white">
            {completedCount} of {visibleSteps.length} steps complete ({progressPercent}%)
          </span>
        </div>
        <div className="h-2 rounded-full bg-gray-900 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              progressPercent === 100 ? 'bg-emerald-500' : 'bg-blue-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          You can complete steps in any order and come back anytime. Estimated
          time remaining: ~{Math.max(0, (visibleSteps.length - completedCount) * 7)}{' '}
          minutes.
        </p>
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {visibleSteps.map((step) => {
          const state =
            data?.steps[step.id] ?? {
              completed: false,
              completedAt: null,
              completedBy: null,
            };
          const expanded = expandedSteps.has(step.id);
          const StepIcon = step.icon;

          return (
            <div
              key={step.id}
              className={`rounded-lg border ${
                state.completed
                  ? 'border-emerald-500/30 bg-emerald-500/5'
                  : 'border-gray-700 bg-gray-800/40'
              } overflow-hidden`}
            >
              <div
                onClick={() => toggleExpanded(step.id)}
                className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-gray-800/60"
              >
                <div
                  className={`flex-shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center text-sm font-bold ${
                    state.completed
                      ? 'bg-emerald-500 text-white'
                      : 'bg-gray-700 text-gray-300'
                  }`}
                >
                  {state.completed ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    step.number
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <StepIcon className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <h3 className="text-sm font-semibold text-white truncate">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    ~{step.estimatedMinutes}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleToggleComplete(step.id, state.completed);
                  }}
                  className={`px-3 py-1 rounded-md text-xs font-medium ${
                    state.completed
                      ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {state.completed ? 'Completed' : 'Mark Complete'}
                </button>
                {expanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                )}
              </div>
              {expanded && (
                <div className="px-5 pb-5 pt-0 border-t border-gray-700/50 bg-gray-900/40">
                  <p className="text-xs text-gray-400 italic my-3">
                    <strong className="text-gray-300">Why:</strong> {step.why}
                  </p>
                  {renderStepBody(step, {
                    ...brandingForm,
                    setBrandColor: (c) =>
                      setBrandingForm({ ...brandingForm, brandColor: c }),
                    setCompanyName: (s) =>
                      setBrandingForm({ ...brandingForm, companyName: s }),
                    setAbn: (s) => {
                      setAbnError(null);
                      setAbnDeclared(false);
                      setBrandingForm({ ...brandingForm, abn: s });
                    },
                    abnError,
                    abnVerification: ctx.companyData?.abnVerification ?? null,
                    abnDeclared,
                    setAbnDeclared: (v) => {
                      setAbnError(null);
                      setAbnDeclared(v);
                    },
                    setAddress: (s) =>
                      setBrandingForm({ ...brandingForm, address: s }),
                    setPhone: (s) =>
                      setBrandingForm({ ...brandingForm, phone: s }),
                    setReplyToEmail: (s) =>
                      setBrandingForm({ ...brandingForm, replyToEmail: s }),
                    replyToError,
                    brandingSaveError,
                    brandingSaved,
                    edmEnabled,
                    canImportEdm,
                    onSaveBranding: handleSaveBranding,
                    saving: savingBranding,
                    logoUrl: data?.branding?.logoUrl ?? null,
                    uploadLogo,
                    saveResendApiKey: handleSaveResendApiKey,
                    // `data.steps.resend.completed` is admin-toggled; deriving
                    // `configured` from just completion risks a confusing
                    // green state before the key is actually stored. For now
                    // treat "completed === true" as the signal — admins click
                    // Mark Complete after saving and the UI flips.
                    resendConfigured: !!data?.steps?.resend?.completed,
                    checkResendDomain: handleCheckResendDomain,
                    resendDomainState,
                    loadGoLiveChecklist: getGoLiveChecklist,
                    companyId,
                    onConnectMeta: () => setMetaModalOpen(true),
                    onConnectGoogle: () => setGoogleModalOpen(true),
                    metaConnected,
                    googleConnected,
                    onConnectMyCrm: () => setMyCrmModalOpen(true),
                    myCrmConnected,
                    brokers,
                    brokersLoading,
                    categories,
                    initialRotations,
                    initialNotify,
                    onSaveRouting: handleSaveRouting,
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConnectMetaModal
        companyId={companyId}
        open={metaModalOpen}
        onClose={() => setMetaModalOpen(false)}
      />
      <ConnectGoogleAdsModal
        companyId={companyId}
        open={googleModalOpen}
        onClose={() => setGoogleModalOpen(false)}
      />
      <ConnectMyCrmModal
        companyId={companyId}
        open={myCrmModalOpen}
        onClose={() => setMyCrmModalOpen(false)}
      />
    </div>
  );
};

export default LeadCaptureSetupWizardPage;
